<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import type {
  IFinanceDeductionPayload,
  IFinanceDeductionSchema,
} from '@modules/finance/submodules/month-report/submodules/deduction/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import { EAasmState } from '@modules/core/enums';
import { TRANSLATION_GROUP_FINANCE } from '@modules/finance/constants';
import {
  ADMIN_FINANCE_MONTH_LIST_ROUTE_NAME,
  AFFILIATE_FINANCE_MONTH_LIST_ROUTE_NAME,
} from '@modules/finance/submodules/month-report/constants';
import { useSaveFinanceMonthDeduction } from '@modules/finance/submodules/month-report/submodules/deduction/mutations';
import { useFinanceDeductionQuery } from '@modules/finance/submodules/month-report/submodules/deduction/queries';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { isString } from '@utils/guards';

const route = useRoute();
const { getTranslation } = useI18n();

const payload = computed<IFinanceDeductionPayload>(() => ({
  affiliate_id: isString(route.params.id) ? Number(route.params.id) : 0,
  date_of_payment: isString(route.params.date) ? route.params.date : '',
}));

const date = computed(() => payload.value.date_of_payment);
const affiliateId = computed(() => payload.value.affiliate_id);

const { data, isLoading, setData } = useFinanceDeductionQuery(payload);
const {
  mutateAsync,
  isLoading: isSaveLoading,
  errors,
  clearError,
} = useSaveFinanceMonthDeduction();

const formSchema = computed<FormSchema<IFinanceDeductionSchema>>(() => ({
  affiliate_username: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_FINANCE, 'DEDUCTION_FORM_AFFILIATE').value,
    readonly: true,
    value: data.value?.deduction.affiliate_username,
  },
  deduction: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_FINANCE, 'DEDUCTION_FORM_SIZE').value,
    value: data.value?.deduction.deduction || 0,
  },
  deduction_commit: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_FINANCE, 'DEDUCTION_FORM_COMMENT').value,
    value: data.value?.deduction.deduction_commit || '',
  },
}));

const isEdit = computed(() => !!data.value?.deduction.deduction || data.value?.deduction.deduction_commit);
const isReadonly = computed(() => (
  data.value?.month_payment_aasm_state !== EAasmState.IN_PROCESS && data.value?.month_payment_aasm_state !== EAasmState.NEW_PLAN
));

const { isAdminNamespace } = useCurrentNamespace();

const monthRouteName = computed(() => (isAdminNamespace.value ? ADMIN_FINANCE_MONTH_LIST_ROUTE_NAME : AFFILIATE_FINANCE_MONTH_LIST_ROUTE_NAME));

async function onSubmit(values: IFinanceDeductionSchema): Promise<void> {
  if (data.value) {
    await mutateAsync({
      affiliate_id: affiliateId.value,
      date_of_payment: date.value,
      deduction: {
        ...data.value.deduction,
        ...values,
        date_of_payment: date.value,
      },
    });

    setData((queryData) => {
      if (!queryData) {
        return queryData;
      }

      return {
        ...queryData,
        deduction: {
          ...queryData.deduction,
          ...values,
        },
      };
    });
  }
}

function onDelete(): void {
  if (data.value) {
    void onSubmit({
      deduction: 0,
      deduction_commit: '',
    });
  }
}
</script>

<template>
  <VCard v-data-qa-auto-id="'FinanceMonthDeductionPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_FINANCE, isEdit ? 'DEDUCTION_FORM_EDIT_DEDUCTION' : 'DEDUCTION_FORM_CREATE_DEDUCTION')"
  >
    <VLoader
      :loading="isLoading"
      min-height="200px"
    >
      <VForm
        :schema="formSchema"
        :is-loading="isSaveLoading"
        :custom-errors="errors"
        :readonly="isReadonly"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_FINANCE, 'DEDUCTION_FORM_SAVE')"
        @interacted="clearError"
        @submit="onSubmit"
      >
        <template #secondaryBtn>
          <VButton
            v-if="isEdit && !isReadonly"
            :label="$getTranslation(TRANSLATION_GROUP_FINANCE, 'DEDUCTION_FORM_DELETE')"
            severity="danger"
            @click="onDelete"
          />
          <VBackButton
            :label="$getTranslation(TRANSLATION_GROUP_FINANCE, 'DEDUCTION_FORM_BACK')"
            :to="{
              name: monthRouteName,
              params: {
                date,
              },
            }"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
